/** Tonight's reads (2.D deliverable 4): one `sessions.tonight` answer feeds the page, the run sheet and the home card. */
import {
  docsTitles,
  type SessionItemV1,
  sessionsItemsList,
  sessionsTonight,
  type TonightV1,
} from '@codex/contracts';
import { useQuery } from '@tanstack/react-query';
import { api } from '../auth/client.js';

export const SESSIONS_KEY = ['sessions'] as const;
export const TONIGHT_KEY = ['sessions', 'tonight'] as const;

export const tonightKey = (sessionId?: string) =>
  ['sessions', 'tonight', sessionId ?? 'auto'] as const;

export function fetchTonight(sessionId?: string): Promise<TonightV1> {
  return api(sessionsTonight, { query: sessionId === undefined ? {} : { sessionId } });
}

export function useTonight(sessionId?: string, enabled = true) {
  return useQuery({
    queryKey: tonightKey(sessionId),
    queryFn: () => fetchTonight(sessionId),
    enabled,
  });
}

/** Page titles for the secrets' pages ("Secret on <page title>"). */
export function useTitles(ids: readonly string[]) {
  const key = [...new Set(ids)].sort();
  return useQuery({
    queryKey: ['sessions', 'titles', key],
    enabled: key.length > 0,
    queryFn: async () => {
      const found = new Map<string, string>();
      const { titles } = await api(docsTitles, { body: { ids: key } });
      for (const item of titles) if (item.title !== null) found.set(item.id, item.title);
      return found;
    },
  });
}

export const itemsKey = (sessionId: string | undefined) =>
  ['sessions', sessionId, 'items'] as const;

/** The session's run sheet (stage s2): fresh row versions after each write. */
export function useRunSheet(sessionId: string | undefined) {
  return useQuery({
    queryKey: itemsKey(sessionId),
    enabled: sessionId !== undefined,
    queryFn: async (): Promise<SessionItemV1[]> =>
      (await api(sessionsItemsList, { params: { id: sessionId as string } })).items,
  });
}
